import { AxiosGlobal } from "../shared/axios";
import { AxiosPromise, AxiosRequestConfig } from "axios";
import configs from "../../configs";
import { store } from "../../Components/store/store";
import { ApiResponse, UserData, KycNextStepData } from "../authApis";
import { createApiHeaders, createPublicApiHeaders } from "../utils/apiHeaders";

/**
 * Authentication Live API implementation
 */
export class AuthLiveApis extends AxiosGlobal {
    /**
     * Gets auth headers with the current token
     */
    private getAuthHeaders(): AxiosRequestConfig {
        // Get token directly from localStorage for reliability
        const token = localStorage.getItem('afripay_token');

        // Only attempt to get from Redux if not found in localStorage
        const reduxToken = store.getState().data.login.value.token;
        // console.log(reduxToken)

        // Use whichever token is available
        const authToken = reduxToken;

        return {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": authToken ? `Bearer ${authToken}` : '',
                "Access-Control-Allow-Origin": "*",
                "X-Browser-Id": "client_H6C2q86BqhICuaQ6naZFzV2qUfodAnLN"
            }
        };
    }

    /**
     * Register a new user
     * @param data Registration form data
     */
    register(data: FormData): AxiosPromise<ApiResponse<UserData>> {
        return this.axios.post(`${configs.context}/auth/${configs.apiList.REGISTER}`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Verify OTP code
     * @param data OTP verification data
     */
    verifyOtp(data: { user_id: string; otp: string }): AxiosPromise<ApiResponse<UserData>> {
        return this.axios.post(`${configs.context}/auth/verify-otp`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Resend OTP code
     * @param data User ID for resending OTP
     */
    resendOtp(data: { user_id: string }): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/auth/resend-otp`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Setup user profile
     * @param data Profile setup data
     */
    setUpProfile(data: FormData): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/auth/setup-profile`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Complete user profile with KYC data
     * @param data KYC profile completion data
     */
    completeProfile(data: any): AxiosPromise<ApiResponse<any>> {
        // return this.axios.post(`${configs.context}/kyc/complete-profile`, data, this.getAuthHeaders());
        return this.axios.post(`${configs.context}/kyc/complete-profile`, data, {
            headers: createApiHeaders()
        });
    }

    /**
     * Save purposes for using the platform
     * @param data User purposes
     */
    savePurposes(data: any): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/auth/save-purposes`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * User login
     * @param data Login credentials
     */
    login(data: { email: string; password: string }): AxiosPromise<ApiResponse<UserData>> {
        return this.axios.post(`${configs.context}/auth/${configs.apiList.LOGIN}`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Get user KYC status
     */
    // getKycStatus(): AxiosPromise<ApiResponse<KycStatusData>> {
    //     return this.axios.get(`${configs.context}/kyc/status`, this.getAuthHeaders());
    // }

    getKycStatus(token: any): AxiosPromise<Array<any>> {
        // console?.log(store.getState().data.login.value.token)
        return this.axios.get(`${configs.context}/kyc/status`, {
            headers: createApiHeaders()
        });
    }

    /**
     * Get next KYC step for the user
     */
    getKycNextStep(): AxiosPromise<ApiResponse<KycNextStepData>> {
        return this.axios.get(`${configs.context}/kyc/next-step`, this.getAuthHeaders());
    }

    /**
     * Create user survey
     * @param data Survey data
     */
    createSurvey(data: any): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/survey/create-survey`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Request password reset
     * @param data User email
     */
    forgotPassword(data: FormData): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/forgot`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
     * Alternative password reset method
     * @param data User email
     */
    forgotPWord(data: FormData): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/auth/forgot`, data, {
            headers: createPublicApiHeaders()
        });
    }

    /**
  * Reset password with token (original method name)
  * @param data Password reset data (either FormData or object)
  * @deprecated Use resetPassword instead for consistency
  */
    resetPWord(data: FormData | { token: string; password: string; password_confirmation: string }): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/auth/reset`, data, this.getAuthHeaders());
    }

    /**
     * Reset password with token (new consistent method name)
     * @param data Password reset data (either FormData or object)
     */
    resetPassword(data: FormData | { token: string; password: string; password_confirmation: string }): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/auth/reset`, data, this.getAuthHeaders());
    }
    /**
     * Verify user email
     * @param data Email verification data
     */
    verifyMail(data: any): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.context}/verify-mail`, data, this.getAuthHeaders());
    }


    /**
     * User logout
     * @param data Logout data
     */
    logout(data: any): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.contextOld}/logout`, data, this.getAuthHeaders());
    }

    saveWaitList(data: any): AxiosPromise<ApiResponse<any>> {
        return this.axios.post(`${configs.contextOld}/save-waitlist`, data, {
            headers: createPublicApiHeaders()
        });
    }
}